import { useState } from 'react'
import { supabase } from '../supabaseClient'
import { useNavigate, Link } from 'react-router-dom'

export default function Register() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [job, setJob] = useState('')
  const [role] = useState('user')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [usernameAvailable, setUsernameAvailable] = useState(null)
  const [checkingUsername, setCheckingUsername] = useState(false)

  const navigate = useNavigate()

  // Check if username is available
  const checkUsernameAvailability = async (usernameValue) => {
    if (!usernameValue || usernameValue.length < 3) {
      setUsernameAvailable(null)
      return
    }

    setCheckingUsername(true)
    try {
      // Use RPC function to bypass RLS policies
      const { data, error } = await supabase
        .rpc('check_username_exists', { username_input: usernameValue.toLowerCase() })


      if (error) {
        console.error('Error checking username:', error)
        setUsernameAvailable(null)
      } else if (data === true) {
        setUsernameAvailable(false) // Username taken
      } else {
        setUsernameAvailable(true) // Username available
      }
    } catch (err) {
      console.error('Exception checking username:', err)
      setUsernameAvailable(null)
    } finally {
      setCheckingUsername(false)
    }
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    setMessage(null)
    
    // Validate username is required and available
    if (!username) {
      setMessage({ type: 'error', text: 'Username wajib diisi.' })
      return
    }
    
    if (username.length < 3) {
      setMessage({ type: 'error', text: 'Username minimal 3 karakter.' })
      return
    }
    
    if (usernameAvailable === false) {
      setMessage({ type: 'error', text: 'Username sudah digunakan. Pilih username lain.' })
      return
    }
    
    setLoading(true)

    // Sign up without email confirmation
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: undefined, // No email verification
        data: {
          full_name: fullName,
          job: job,
          role: role
        }
      }
    })

    if (signUpError) {
      setMessage({ type: 'error', text: signUpError.message })
      setLoading(false)
      return
    }

    const userId = data?.user?.id
    const avatarUrl = `${window.location.origin}/assets/images/default-avatar.png`

    // Insert user data into users table with pending status
    if (userId) {
      const { error: insertError } = await supabase.from('users').insert([
        {
          id: userId,
          email,
          username: username.toLowerCase(),
          full_name: fullName,
          job,
          role,
          status: 'pending', // Menunggu approval admin
          photo: avatarUrl,
          created_at: new Date().toISOString()
        }
      ])

      // Check for duplicate username error (code 23505 = unique violation)
      if (insertError) {
        console.error('Insert error:', insertError)
        
        if (insertError.code === '23505' && insertError.message?.includes('username')) {
          // Username duplicate - delete auth user and show error
          await supabase.auth.admin.deleteUser(userId)
          setMessage({ 
            type: 'error', 
            text: '❌ Username sudah digunakan oleh user lain. Silakan pilih username yang berbeda.' 
          })
          setLoading(false)
          return
        }
        
        // Other errors - continue but log
        // Don't block registration if user was created in auth
      }
    }

    // Logout user karena status pending (belum approved)
    await supabase.auth.signOut()

    // Langsung redirect ke login
    navigate('/login', { 
      state: { 
        message: '✅ Registrasi berhasil! Akun Anda telah dibuat dan sedang menunggu verifikasi dari administrator. Anda akan menerima akses setelah akun Anda disetujui. Terima kasih atas kesabaran Anda.' 
      }
    })
    
    setLoading(false)
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white flex flex-col">
      <div className="flex flex-1">
      {/* Left Side - Background Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/assets/backgrounds/login-background-alt.png')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        >
          {/* Dark Overlay for better contrast */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-pink-900/40 to-blue-900/40"></div>
        </div>

        {/* Floating Particles on Image Side */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-3 h-3 bg-white/40 rounded-full animate-float shadow-lg"></div>
          <div className="absolute top-40 right-20 w-4 h-4 bg-blue-300/40 rounded-full animate-float-delayed shadow-lg"></div>
          <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-pink-300/40 rounded-full animate-float-slow shadow-lg"></div>
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-blue-300/40 rounded-full animate-float shadow-lg"></div>
          <div className="absolute bottom-20 right-10 w-2 h-2 bg-cyan-300/40 rounded-full animate-float-delayed shadow-lg"></div>
          <div className="absolute top-1/2 left-20 w-4 h-4 bg-blue-300/40 rounded-full animate-float-slow shadow-lg"></div>
          <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-blue-400/40 rounded-full animate-float shadow-lg"></div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-white">
            <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">Join Us Today!</h1>
            <p className="text-xl text-white/90 drop-shadow-md">Create an account to get started</p>
          </div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 relative overflow-hidden">
      {/* Futuristic Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-200/40 via-pink-200/40 to-blue-200/40 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-cyan-200/40 via-blue-200/40 to-blue-200/40 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-100/30 via-blue-100/30 to-pink-100/30 rounded-full blur-3xl animate-float-slow"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>

        {/* Floating Particles */}
        <div className="absolute top-32 right-10 w-2 h-2 bg-blue-400/30 rounded-full animate-float"></div>
        <div className="absolute top-2/3 left-20 w-3 h-3 bg-pink-400/30 rounded-full animate-float-delayed"></div>
        <div className="absolute bottom-40 right-1/4 w-2 h-2 bg-blue-400/30 rounded-full animate-float-slow"></div>
        <div className="absolute top-1/4 left-1/3 w-3 h-3 bg-cyan-400/30 rounded-full animate-float"></div>
        <div className="absolute bottom-32 left-10 w-2 h-2 bg-blue-400/30 rounded-full animate-float-delayed"></div>
      </div>

      {/* Register Card Container */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Glass Morphism Card */}
          <div className="relative backdrop-blur-xl bg-white/70 p-10 rounded-3xl shadow-2xl border border-white/40">
            {/* Shimmer Effect on Card Border */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer pointer-events-none"></div>
            
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-pink-400 rounded-2xl blur-xl opacity-30 animate-pulse"></div>
                <img 
                  src="/assets/logos/logo-variant-4.png" 
                  alt="Logo" 
                  className="relative h-32 w-auto object-contain drop-shadow-lg"
                />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-pink-600 to-blue-600 bg-clip-text text-transparent mb-2">
                Bergabung Sekarang
              </h2>
              <p className="text-slate-500 text-sm">Buat akun untuk mengakses sistem</p>
            </div>

            {/* Message Alert */}
            {message && (
              <div
                className={`mb-6 px-4 py-4 rounded-xl text-sm font-medium transition-all duration-300 backdrop-blur-sm ${
                  message.type === 'success'
                    ? 'bg-green-100/80 text-green-700 border border-green-200/50 shadow-lg shadow-green-100/50'
                    : 'bg-red-100/80 text-red-700 border border-red-200/50 shadow-lg shadow-red-100/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {message.type === 'success' ? (
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  <p>{message.text}</p>
                </div>
              </div>
            )}

            {/* Avatar Preview */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-400 rounded-full blur-lg opacity-30"></div>
                <img
                  src="/assets/images/default-avatar.png"
                  alt="Default Avatar"
                  className="relative w-20 h-20 rounded-full border-2 border-white shadow-xl"
                />
              </div>
            </div>

            {/* Register Form */}
            <form onSubmit={handleRegister} className="space-y-4">
              {/* Full Name Input */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-pink-400 rounded-xl opacity-0 group-focus-within:opacity-30 blur transition duration-300"></div>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Nama Lengkap"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="relative w-full bg-white/50 backdrop-blur-sm border border-slate-200 pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Username Input */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-blue-400 rounded-xl opacity-0 group-focus-within:opacity-30 blur transition duration-300"></div>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => {
                      const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '')
                      setUsername(cleaned)
                      if (cleaned.length >= 3) {
                        checkUsernameAvailability(cleaned)
                      } else {
                        setUsernameAvailable(null)
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value.length >= 3) {
                        checkUsernameAvailability(e.target.value)
                      }
                    }}
                    required
                    minLength={3}
                    maxLength={30}
                    pattern="[a-z0-9_\-]{3,30}"
                    title="Username harus 3-30 karakter, hanya huruf kecil, angka, dash (-) dan underscore (_)"
                    className="relative w-full bg-white/50 backdrop-blur-sm border border-slate-200 pl-12 pr-12 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                  />
                  {/* Availability indicator */}
                  {username.length >= 3 && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      {checkingUsername ? (
                        <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      ) : usernameAvailable === true ? (
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : usernameAvailable === false ? (
                        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      ) : null}
                    </div>
                  )}
                </div>
                {/* Status message - outside input but inside group */}
                <div className="mt-1.5 px-1 space-y-1">
                  <p className="text-xs text-slate-500">3-30 karakter: huruf kecil, angka, dash, underscore</p>
                  {username.length >= 3 && (
                    <>
                      {checkingUsername ? (
                        <p className="text-xs text-blue-600 font-medium flex items-center gap-1">
                          <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                          Mengecek ketersediaan...
                        </p>
                      ) : usernameAvailable === true ? (
                        <p className="text-xs text-green-600 font-medium">✓ Username tersedia</p>
                      ) : usernameAvailable === false ? (
                        <p className="text-xs text-red-600 font-medium">✗ Username sudah digunakan</p>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              {/* Email Input */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-xl opacity-0 group-focus-within:opacity-30 blur transition duration-300"></div>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="relative w-full bg-white/50 backdrop-blur-sm border border-slate-200 pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-400 to-blue-400 rounded-xl opacity-0 group-focus-within:opacity-30 blur transition duration-300"></div>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-pink-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="relative w-full bg-white/50 backdrop-blur-sm border border-slate-200 pl-12 pr-32 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400/50 focus:border-pink-400 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm text-pink-600 hover:text-blue-600 font-medium cursor-pointer select-none transition-colors duration-300"
                  >
                    {showPassword ? 'Sembunyikan' : 'Tampilkan'}
                  </button>
                </div>
              </div>

              {/* Job Input */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl opacity-0 group-focus-within:opacity-30 blur transition duration-300"></div>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-cyan-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Pekerjaan (Opsional)"
                    value={job}
                    onChange={(e) => setJob(e.target.value)}
                    className="relative w-full bg-white/50 backdrop-blur-sm border border-slate-200 pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Register Button */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full group overflow-hidden mt-6"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-pink-600 to-blue-600 rounded-xl transition-all duration-300 group-hover:shadow-xl group-hover:shadow-pink-500/50"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-pink-600 to-blue-600 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-all duration-300"></div>
                <div className="relative px-6 py-3.5 text-white font-semibold flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Mendaftarkan...</span>
                    </>
                  ) : (
                    <>
                      <span>Daftar Sekarang</span>
                      <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </div>
              </button>
            </form>

            {/* Back to Login */}
            <div className="text-center mt-6">
              <Link
                to="/login"
                className="text-sm text-slate-600 hover:text-slate-800 transition-colors inline-flex items-center gap-1 group"
              >
                <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Kembali ke Login</span>
              </Link>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-pink-400/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-tl from-blue-400/20 to-cyan-400/20 rounded-full blur-2xl"></div>
          </div>

          {/* Footer Text */}
          <div className="text-center mt-8">
            <p className="text-xs text-slate-400">
              © 2024 • Sistem Manajemen Karyawan • Secure & Reliable
            </p>
          </div>
        </div>
      </div>
      </div>
      </div>
    </div>
  )
}
